/**
 * The ONE dense-step DFS over the resolved process IR (`Flow`) — the shared
 * traversal that the graph (edges) and the render walker (`WalkResult`) both
 * consume instead of each hand-mirroring the stepIndex / `do`-inlining / branch-
 * selection scheme in lock-step (the bug class IR convergence kills; plan
 * 2026-06-29 Stages A / 6 / 7).
 *
 * The emitter OWNS: document-order traversal, the dense `stepIndex` (the
 * persisted viewer-state contract), branch SELECTION (which branches emit — and
 * therefore which consume an index), `do`-inlining (recursion guard +
 * `substituteTemplateCallerIR`), opaque session-only `nodeId`s, and the
 * collapsed/parallel index RANGES. It emits a structural event stream:
 *   - `step` — a call HOP / note / `go` / `await` (consumes one index);
 *   - `terminator` — `fail` / `finish` (a tree node, no index);
 *   - `open` / `close` — a container scope (+ range/expanded/recursive on close);
 *   - `group` / `groupEnd` — a sub-body within a container (arm / case / catch /
 *     branch / body …) carrying its `selected` flag;
 *   - `diagnostic` — a `do` that could not inline (recursion / unknown target).
 *
 * Consumers decide MEANING: the graph subscribes to `step` only (call hops →
 * edges; notes/go/await still advance the shared counter, keeping edge indices
 * aligned with the walker); the walker assembles the `WalkResult` tree + lane
 * modifiers from the full stream. The emitter knows nothing of edges or
 * `WalkNode`s.
 */

import type {
  Flow,
  ProcessNode,
  CallHop,
  CallNode,
  NoteNode,
  AwaitNode,
  ReentryNode,
  TerminatorNode,
  SpliceNode,
  SubprocessModel,
} from "./process-model.js";
import { substituteTemplateCallerIR } from "./process-ir-walk.js";
import { demoteMultiAwaitHead, awaitClauseTimerFlow } from "./process-ir-desugar.js";

/** One index-consuming unit emitted in document (DFS) order. */
export interface EmittedStep {
  readonly kind: "call" | "note" | "prose" | "go" | "await";
  readonly stepIndex: number;
  /** Opaque, unique, session-only id — its string form is not a contract. */
  readonly nodeId: string;
  /** The source IR node (the CallNode for a hop, else the note/await/reentry). */
  readonly node: ProcessNode;
  /** Present for `kind === "call"` — the specific hop (one EmittedStep per hop). */
  readonly hop?: CallHop;
  /** Index of `hop` within its CallNode (0 = chain head). */
  readonly hopIndex?: number;
  /** True for the final hop of its CallNode (carries the bind / `as` name). */
  readonly isLastHop?: boolean;
  /** Branch condition / select case / catch label / "else" / "unwind" in scope. */
  readonly altCondition?: string;
  /** `do` invocation chain enclosing this unit (outermost first); empty at root. */
  readonly chain: readonly string[];
}

/** A container scope opening/closing. Range + expanded/recursive are filled on
 *  `close` (parallel/splice) so the consumer can record index ranges. */
export interface EmitContainer {
  readonly irKind: ProcessNode["kind"];
  readonly nodeId: string;
  readonly node: ProcessNode;
  /** Step index when the scope opened (for range recording on close). */
  readonly startIndex: number;
  /** Last step index inside the scope (`< startIndex` ⇒ empty); set on close. */
  readonly endIndex?: number;
  /** `do` only: user-expanded (collapsed otherwise) / recursion-guarded. */
  readonly expanded?: boolean;
  readonly recursive?: boolean;
}

/** A sub-body within a container (an `if` arm, `select` case, `catch`, a loop
 *  body, a parallel branch, a reversible item/unwind …). */
export interface EmitGroup {
  readonly role:
    | "body" | "arm" | "else" | "case" | "attempt" | "catch" | "elseBody"
    | "branch" | "fanout" | "item" | "unwind";
  readonly nodeId: string;
  readonly label?: string;
  /** False for an unselected `if` arm (emitted only under `showAllBranches`). */
  readonly selected: boolean;
  /** Reversible `item` / `unwind` groups only — the source item index (`ri`),
   *  read structurally by consumers (never by parsing the nodeId). Lets the
   *  walker pair a forward item with its compensation across the reordered
   *  emission (forwards in item order, unwinds in reverse). */
  readonly itemIndex?: number;
}

/** A `do` that could not be inlined — recursion (target on the chain) or an
 *  unknown target. The walker surfaces these; the graph ignores them. */
export interface EmitDiagnostic {
  readonly kind: "recursion" | "unknown-subprocess";
  readonly target: string;
  readonly chain: readonly string[];
  readonly nodeId: string;
}

/** A `do` that RESOLVED and is being inlined — the invocation itself, fired
 *  once per successful splice entry (before its body walks). The graph turns
 *  this into a `do` edge (invoker process → spliced subprocess/process); the
 *  step consumers that only care about the inlined bodies ignore it. Recursion /
 *  unknown targets never fire this (they surface as {@link EmitDiagnostic}). */
export interface EmitSplice {
  /** Splice target as written (`SpliceNode.target` joined) — the key the
   *  invoker resolved through, and the last element pushed onto child chains. */
  readonly target: string;
  /** Enclosing splice chain (outermost first); empty ⇒ spliced at process root.
   *  Its last element (when present) is THIS splice's immediate invoker. */
  readonly chain: readonly string[];
  /** Step index at splice entry — the index of the first inlined step. */
  readonly stepIndex: number;
  readonly nodeId: string;
  /** Branch / case / catch / unwind label in scope, if any. */
  readonly altCondition?: string;
  readonly node: SpliceNode;
}

export interface EmitVisitor {
  step(u: EmittedStep): void;
  terminator?(node: TerminatorNode, nodeId: string): void;
  open?(c: EmitContainer): void;
  close?(c: EmitContainer): void;
  group?(g: EmitGroup): void;
  groupEnd?(g: EmitGroup): void;
  diagnostic?(d: EmitDiagnostic): void;
  splice?(s: EmitSplice): void;
}

export interface EmitOpts {
  /** Subprocesses callable here, keyed by name (do-target lookup + inlining). */
  /** Splice-expansion targets by name. Subprocesses AND (v0.9.0 §2.5)
   *  processes — the map builder guarantees a same-name subprocess wins.
   *  Only `body` / `templateParam` are consumed here, so both model kinds fit. */
  readonly subModels: ReadonlyMap<
    string,
    Pick<SubprocessModel, "body"> & Partial<Pick<SubprocessModel, "templateParam">>
  >;
  /** Emit every branch (graph / Flow view). Default true. When false, only the
   *  selected `if` arm's children are walked (unselected arms emit an empty
   *  group — no steps, no index), matching the default walker. */
  readonly showAllBranches?: boolean;
  /** `if` selection: container nodeId → chosen arm label. */
  readonly branchSelection?: ReadonlyMap<string, string>;
  /** `do` nodeIds the user has expanded (their inlined body shows in the tree). */
  readonly expandedSubprocesses?: ReadonlySet<string>;
  /** Root id for nodeId derivation (e.g. `p:<processId>`). */
  readonly rootId?: string;
}

/** Walk a process/subprocess body, emitting dense steps + structure in order. */
export function emitSteps(flow: Flow | undefined, opts: EmitOpts, visitor: EmitVisitor): void {
  let counter = 0;
  const next = (): number => counter++;
  const showAll = opts.showAllBranches ?? true;
  const selection = opts.branchSelection;
  const expandedSet = opts.expandedSubprocesses;
  const root = opts.rootId ?? "p";

  // Opaque nodeId — IR-native (parent / irKind : positional index). Unique +
  // stable within a result; never persisted, never parsed (verified), so the
  // exact string is not a contract.
  const idOf = (parent: string, kind: string, i: number): string => `${parent}/${kind}:${i}`;

  const recurGroup = (
    f: Flow | undefined,
    role: EmitGroup["role"],
    groupId: string,
    label: string | undefined,
    selected: boolean,
    walk: boolean,
    alt: string | undefined,
    chain: readonly string[],
    itemIndex?: number,
  ): void => {
    const g: EmitGroup = { role, nodeId: groupId, label, selected, ...(itemIndex != null ? { itemIndex } : {}) };
    visitor.group?.(g);
    if (walk) {
      let i = 0;
      for (const n of f ?? []) visit(n, groupId, i++, alt, chain);
    }
    visitor.groupEnd?.(g);
  };

  const container = (
    n: ProcessNode,
    nodeId: string,
    body: (startIndex: number) => void,
    extra?: { expanded?: boolean; recursive?: boolean },
  ): void => {
    const startIndex = counter;
    visitor.open?.({ irKind: n.kind, nodeId, node: n, startIndex, ...extra });
    body(startIndex);
    visitor.close?.({ irKind: n.kind, nodeId, node: n, startIndex, endIndex: counter - 1, ...extra });
  };

  const visit = (n: ProcessNode, parent: string, i: number, alt: string | undefined, chain: readonly string[]): void => {
    const nodeId = idOf(parent, n.kind, i);
    switch (n.kind) {
      case "call": {
        const c = n as CallNode;
        const last = c.hops.length - 1;
        c.hops.forEach((hop, hopIndex) =>
          visitor.step({
            kind: "call", stepIndex: next(), nodeId: `${nodeId}/hop:${hopIndex}`,
            node: c, hop, hopIndex, isLastHop: hopIndex === last, altCondition: alt, chain,
          }),
        );
        break;
      }
      case "note":
        visitor.step({ kind: "note", stepIndex: next(), nodeId, node: n as NoteNode, altCondition: alt, chain });
        break;
      case "prose":
        // v0.9.0 §2.6: positional flow prose — advances the index like a note
        // so walker/graph stay lock-step, but carries no owner and no edge.
        visitor.step({ kind: "prose", stepIndex: next(), nodeId, node: n, altCondition: alt, chain });
        break;
      case "await":
        visitor.step({ kind: "await", stepIndex: next(), nodeId, node: n as AwaitNode, altCondition: alt, chain });
        break;
      case "reentry":
        visitor.step({ kind: "go", stepIndex: next(), nodeId, node: n as ReentryNode, altCondition: alt, chain });
        break;
      case "terminator":
        visitor.terminator?.(n as TerminatorNode, nodeId);
        break;
      case "description":
        break;
      case "loop":
        container(n, nodeId, () => recurGroup(n.body, "body", `${nodeId}/body`, undefined, true, true, alt, chain));
        break;
      case "branch":
        container(n, nodeId, () => {
          const aspects = n.arms.map((a) => a.condition?.text ?? "");
          const chosen = selection?.get(nodeId) ?? aspects[0] ?? "else";
          n.arms.forEach((a, ai) => {
            const label = a.condition?.text ?? ""; // walker branchLabel
            const sel = label === chosen;
            // Graph altCondition keeps the parent when no condition (`?? alt`),
            // distinct from the (possibly empty) branchLabel.
            recurGroup(a.body, "arm", `${nodeId}/arm:${ai}`, label, sel, sel || showAll, a.condition?.text ?? alt, chain);
          });
          if (n.elseArm) {
            const sel = chosen === "else";
            recurGroup(n.elseArm.body, "else", `${nodeId}/else`, "else", sel, sel || showAll, "else", chain);
          }
        });
        break;
      case "select":
        container(n, nodeId, () =>
          n.cases.forEach((c, ci) =>
            recurGroup(c.body, "case", `${nodeId}/case:${ci}`, c.label.text, true, true, c.label.text, chain),
          ),
        );
        break;
      case "retry":
        container(n, nodeId, () => {
          recurGroup(n.attempt, "attempt", `${nodeId}/attempt`, undefined, true, true, alt, chain);
          n.catches.forEach((c, ci) =>
            recurGroup(c.body, "catch", `${nodeId}/catch:${ci}`, c.label?.text ?? "catch", true, true, c.label?.text ?? "catch", chain),
          );
          if (n.elseBody) recurGroup(n.elseBody, "elseBody", `${nodeId}/elseBody`, "else", true, true, "else", chain);
        });
        break;
      case "guard":
        // A standalone `try` is SELECTABLE (try vs a catch), like `if` — only the
        // chosen arm's children walk under default selection. (The `try`
        // synthesized inside a `retry` is always-all; that is the `retry` case.)
        container(n, nodeId, () => {
          const aspects = ["try", ...n.catches.map((c) => c.label?.text ?? "catch")];
          const chosen = selection?.get(nodeId) ?? aspects[0] ?? "try";
          const trySel = chosen === "try";
          recurGroup(n.attempt, "attempt", `${nodeId}/attempt`, "try", trySel, trySel || showAll, alt, chain);
          n.catches.forEach((c, ci) => {
            const label = c.label?.text ?? "catch";
            const sel = label === chosen;
            recurGroup(c.body, "catch", `${nodeId}/catch:${ci}`, label, sel, sel || showAll, label, chain);
          });
        });
        break;
      case "parallel":
        container(n, nodeId, () => {
          if (n.body.form === "fanOut") {
            recurGroup(n.body.body, "fanout", `${nodeId}/fanout`, "each", true, true, alt, chain);
          } else {
            n.body.branches.forEach((b, bi) =>
              recurGroup(b.body, "branch", `${nodeId}/branch:${bi}`, `branch:${bi}`, true, true, alt, chain),
            );
            // v0.9.0 §2.2: the await-clause desugars HERE (walk/graph only —
            // the printer round-trips the sugar from the IR): a synthetic
            // timer branch of `await <head>` followed by the else body, or a
            // synthesized `fail "<head>"` when no else was written.
            if (n.awaitClause) {
              const bi = n.body.branches.length;
              recurGroup(awaitClauseTimerFlow(n), "branch", `${nodeId}/branch:${bi}`, `branch:${bi}`, true, true, alt, chain);
            }
          }
        });
        break;
      case "reversible":
        // Transparent in the tree, but emitted as TWO runs so the flow view can
        // show the happy path first and the compensation as a reverse chain:
        //   pass 1 — every item's forward step, in item order;
        //   pass 2 — every item's `unwind`, in REVERSE item order (the order
        //            compensations fire when a later step fails).
        // The walker flags unwind steps as compensation and pairs each with its
        // forward via `itemIndex`.
        container(n, nodeId, () => {
          n.items.forEach((it, ri) => {
            const fwd = it.forward;
            if (fwd) recurGroup(fwd.kind === "block" ? fwd.body : [fwd], "item", `${nodeId}/item:${ri}`, undefined, true, true, alt, chain, ri);
          });
          for (let ri = n.items.length - 1; ri >= 0; ri--) {
            const a = n.items[ri]!.unwind?.action;
            if (!a) continue;
            if (a.kind === "block") recurGroup(a.body, "unwind", `${nodeId}/unwind:${ri}`, undefined, true, true, "unwind", chain, ri);
            else recurGroup([a], "unwind", `${nodeId}/unwind:${ri}`, undefined, true, true, "unwind", chain, ri);
          }
        });
        break;
      case "owner_block":
        // Transparent: children emit at the parent level.
        container(n, nodeId, () => recurGroup(n.body, "body", `${nodeId}/body`, undefined, true, true, alt, chain));
        break;
      case "splice":
        visitSplice(n as SpliceNode, nodeId, alt, chain);
        break;
      default: {
        const _x: never = n;
        void _x;
      }
    }
  };

  const visitSplice = (n: SpliceNode, nodeId: string, alt: string | undefined, chain: readonly string[]): void => {
    const target = n.target.join(".");
    const sub = opts.subModels.get(target);
    if (!sub) {
      visitor.diagnostic?.({ kind: "unknown-subprocess", target, chain, nodeId });
      container(n, nodeId, () => {}, { expanded: false });
      return;
    }
    if (chain.includes(target)) {
      visitor.diagnostic?.({ kind: "recursion", target, chain, nodeId });
      container(n, nodeId, () => {}, { expanded: false, recursive: true });
      return;
    }
    const expanded = expandedSet?.has(nodeId) ?? false;
    const substituted =
      sub.templateParam && n.templateCaller?.form === "explicit" && n.templateCaller.resolved
        ? substituteTemplateCallerIR(sub.body, sub.templateParam.name, n.templateCaller.resolved)
        : sub.body;
    const body = demoteMultiAwaitHead(substituted);
    container(
      n,
      nodeId,
      (startIndex) => {
        // The invocation itself — a resolved `do`, distinct from the inlined
        // body's steps. Fired here (not on recursion/unknown) so the graph
        // derives one `do` edge per real splice; `startIndex` anchors it.
        visitor.splice?.({ target, chain, stepIndex: startIndex, nodeId, altCondition: alt, node: n });
        recurGroup(body, "body", `${nodeId}/body`, target, true, true, alt, [...chain, target]);
      },
      { expanded },
    );
  };

  // Top-level body is the implicit root group.
  // §2.5's any-of demotion applies to the ROOT head exactly as to a splice:
  // consecutive leading awaits are ONE any-of start, so the walk/graph shape is
  // a race over them — two sequential waits would flip OR into AND. (The
  // printer round-trips the authored sugar from the IR; this is walk-only.)
  recurGroup(demoteMultiAwaitHead(flow ?? []), "body", root, undefined, true, true, undefined, []);
}
